<template>
  <div :class="['progress-bar-container', props.file.status]">
    <div class="info-container">
      <div class="file-name-container">
        <DocumentIconComponent :fileName="file.name" :isDirectory="file.isDirectory" />
        <span class="file-name">{{ file.path }}{{ file.name }}</span>
        <span class="file-size" v-if="fileSize">{{ fileSize }}</span>
      </div>
      <div class="status-container">
        <span class="status-info">{{ infoText }}</span>
        <span :class="[computedInfoIcon, 'status-icon']" />
      </div>
    </div>

    <v-progress-linear :indeterminate="indeterminate" rounded :color="vars.progress_bar_success"
      :background-color="backgroundColor" />

    <span v-if="showError" class="error-text">{{ texts.errorText }} {{ file.message }}</span>
  </div>
</template>
<script lang="ts" setup>
import { computed, PropType } from 'vue'
import { FileInfo, FileStatus } from '../UploaderTypes'
import { Icons } from '@/icons/icons'
import { useI18n } from 'vue-i18n-composable'
import { vars } from '@/styles/styleVars'
import DocumentIconComponent from './DocumentIconComponent.vue'
import { formatBytes } from '@/helpers/file'

// DEFINE PROPS  ======================================================================================================
const props = defineProps({
  file: {
    type: Object as PropType<FileInfo>,
    required: true
  }
})
// TRANSLATIONS
const { t } = useI18n()

const backgroundColors = {
  [FileStatus.LOADED]: vars.progress_bar_success,
  [FileStatus.ERROR]: vars.progress_bar_error,
  [FileStatus.LOADING]: vars.progress_bar_default_color
}

const texts = {
  error: t('components.progress_bar.error'),
  errorText: t('components.progress_bar.error_text'),
  loadingText: t('components.progress_bar.executing'),
  successText: t('components.progress_bar.completed')
}

const status = {
  [FileStatus.LOADED]: texts.successText,
  [FileStatus.ERROR]: texts.error,
  [FileStatus.LOADING]: texts.loadingText
}

const iconStatus = {
  [FileStatus.LOADED]: Icons.CHECK_ROUND_FULL,
  [FileStatus.LOADING]: Icons.SPINNER,
  [FileStatus.ERROR]: Icons.CLOSE_ROUND_FULL
}

// COMPUTEDS ==========================================================================================================
const computedInfoIcon = computed(() => iconStatus[props.file.status as keyof typeof iconStatus])
const infoText = computed(() => status[props.file.status as keyof typeof status])
const backgroundColor = computed(() => backgroundColors[props.file.status as keyof typeof backgroundColors])
const indeterminate = computed(() => props.file.status === FileStatus.LOADING)
const showError = computed(() => props.file.status === FileStatus.ERROR)
const fileSize = computed(() => props.file.file?.size && formatBytes(props.file.file?.size || 0))
</script>

<style lang="scss" scoped>
@include file-icons($container-name: 'progress-bar-container');

.progress-bar-container {
  margin-top: $spacing-md;

  .info-container {
    display: flex;
    align-items: center;
    gap: $spacing-xxs;
    align-self: stretch;
    margin-bottom: $spacing-xs;
    width: 100%;
    justify-content: space-between;

    .file-name-container {
      display: flex;
      align-items: flex-start;
      flex: 1 0 0;
      max-width: 85%;

      .file-icon {
        display: block;
        width: 22px;
        height: 22px;
      }

      .file-name {
        @include milano-medium-18;
        color: $neutral-grey-900;
        text-align: left;
        margin-left: $spacing-xs;
        max-width: 80%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .file-size {
        margin-top: 3px;
        margin-left: 15px;
        font-size: 12px;
        color: $neutral-grey-500;
      }
    }

    .status-container {
      display: flex;
      align-items: center;
      gap: $spacing-xxs;

      .status-info {
        @include milano-regular-16;
        color: $neutral-grey-600;
      }

      .status-icon {
        margin-left: $spacing-xxs;
      }
    }
  }

  .error-text {
    @include milano-regular-14;
    color: $tertiary-persianRed-200;
    margin-top: $spacing-xxs;
  }

  &.Error {
    .info-container {
      .status-container {
        .status-info {
          color: $tertiary-persianRed-100;
        }

        .status-icon::before {
          color: $tertiary-persianRed-100;
        }
      }
    }
  }

  &.Loaded {
    .info-container {
      .status-container {
        .status-info {
          color: $tertiary-seaGreen-200;
        }

        .status-icon::before {
          color: $tertiary-seaGreen-200;
        }
      }
    }
  }
}
</style>
